import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Zap, Loader2, CheckCircle2, AlertTriangle, Lock } from "lucide-react";

// Public confirmation page for the one-tap "Apply on Autopilot" email deep-link.
// The email button links here with a signed token. Reachable WITHOUT login — the token
// authorizes the action. The real apply only fires on the Confirm click (POST), never on
// page-load (GET is side-effect-free), so email link-prefetchers can't trigger it.
const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center px-4 py-10">
    <div className="w-full max-w-md bg-white rounded-2xl border border-border shadow-sm p-8 text-center">
      {children}
    </div>
  </div>
);

export default function ApplyConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading");   // loading | invalid | ready | not_premium | applied | already | skipped
  const [job, setJob] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    publicApi.get(`/public/apply-action?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setJob(data.job || {});
        if (data.already_applied) setState("already");
        else if (!data.premium) setState("not_premium");
        else setState("ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const act = async (action) => {
    setBusy(true); setErr("");
    try {
      const { data } = await publicApi.post("/public/apply-action", { token, action });
      if (action === "skip") setState("skipped");
      else if (data.already_applied) setState("already");
      else { setJob((j) => ({ ...j, company: data.company || j.company, title: data.title || j.title })); setState("applied"); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((d && (d.message || d)) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const jobLine = <span className="font-semibold text-[#0F172A]">{job.title || "this role"}{job.company ? ` at ${job.company}` : ""}</span>;
  const tracker = <Link to="/apply/tracker" className="text-[#0025D8] font-semibold hover:underline">View in Applications →</Link>;

  if (state === "loading") return <Shell><Loader2 className="w-8 h-8 animate-spin text-[#0025D8] mx-auto" /></Shell>;

  if (state === "invalid") return (
    <Shell>
      <AlertTriangle className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" />
      <h1 className="font-display font-bold text-xl text-[#0F172A] mb-1">Link expired</h1>
      <p className="text-sm text-muted-foreground">This apply link is invalid or has expired. Open your latest job-matches email, or apply from <Link to="/apply" className="text-[#0025D8] font-semibold hover:underline">Autopilot</Link>.</p>
    </Shell>
  );

  if (state === "not_premium") return (
    <Shell>
      <Lock className="w-10 h-10 text-[#7C3AED] mx-auto mb-3" />
      <h1 className="font-display font-bold text-xl text-[#0F172A] mb-1">Apply on Autopilot is Premium</h1>
      <p className="text-sm text-muted-foreground mb-4">Upgrade to auto-tailor your résumé and apply in one tap.</p>
      <Link to="/dashboard" className="inline-block bg-[#0025D8] text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-[#001CB3]">Upgrade to Premium</Link>
    </Shell>
  );

  if (state === "already") return (
    <Shell>
      <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
      <h1 className="font-display font-bold text-xl text-[#0F172A] mb-1">Already applied</h1>
      <p className="text-sm text-muted-foreground mb-4">You've already applied to {jobLine}. We won't apply twice.</p>
      {tracker}
    </Shell>
  );

  if (state === "applied") return (
    <Shell>
      <div className="w-14 h-14 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-3">
        <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
      </div>
      <h1 className="font-display font-bold text-xl text-[#0F172A] mb-1">Applied! 🎉</h1>
      <p className="text-sm text-muted-foreground mb-4">We tailored your résumé + cover letter and applied to {jobLine}.</p>
      {tracker}
    </Shell>
  );

  if (state === "skipped") return (
    <Shell>
      <h1 className="font-display font-bold text-xl text-[#0F172A] mb-1">Skipped</h1>
      <p className="text-sm text-muted-foreground">No problem — we didn't apply to {jobLine}.</p>
    </Shell>
  );

  // ready → confirmation
  return (
    <Shell>
      <div className="w-14 h-14 rounded-2xl bg-[#EA580C]/10 flex items-center justify-center mx-auto mb-4">
        <Zap className="w-7 h-7 text-[#EA580C]" />
      </div>
      <h1 className="font-display font-bold text-xl text-[#0F172A] mb-1">Apply on Autopilot?</h1>
      <p className="text-sm text-muted-foreground mb-1">We'll tailor your résumé + cover letter and apply to</p>
      <p className="text-sm mb-5">{jobLine}</p>
      {busy ? (
        <div className="flex items-center justify-center gap-2 text-sm text-[#0025D8] py-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Applying on Autopilot… (a few seconds)
        </div>
      ) : (
        <>
          <button onClick={() => act("apply")}
            className="w-full bg-[#0025D8] text-white font-semibold text-sm px-5 py-3 rounded-lg hover:bg-[#001CB3] transition-colors">
            <Zap className="w-4 h-4 inline -mt-0.5 mr-1" /> Confirm & apply
          </button>
          <button onClick={() => act("skip")} className="w-full mt-2 text-sm text-muted-foreground hover:text-[#0F172A] py-2">No thanks</button>
        </>
      )}
      {err && <p className="text-xs text-[#E11D48] mt-3">{err}</p>}
      <p className="text-[11px] text-muted-foreground mt-5 leading-relaxed">
        By confirming, you authorize VetraHub to prepare and submit this application on your behalf using your résumé and profile.
      </p>
    </Shell>
  );
}
